import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Share2, Download, Copy, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';

/**
 * ShareMapMenu — small dropdown with three actions:
 *   1. Download PNG (client-side, captures the provided mapRef)
 *   2. Copy public link
 *   3. Share to WhatsApp (opens WhatsApp web/app with pre-filled text + link)
 *
 * Props:
 *   mapRef       — React ref pointing at the map container DOM node
 *   shareUrl     — absolute URL to share (required for copy/whatsapp)
 *   whatsappText — default WhatsApp message preamble (the url is appended)
 *   filename     — PNG filename (without extension)
 */
const ShareMapMenu = ({ mapRef, shareUrl, whatsappText, filename = 'pollen-dots-map', size = 'sm', variant = 'outline', label = 'Share' }) => {
  const [busy, setBusy] = useState(false);

  const downloadPng = async () => {
    if (!mapRef?.current) { toast.error('Map not ready yet'); return; }
    setBusy(true);
    try {
      // Give Leaflet tiles a beat to settle before snapshotting.
      await new Promise(r => setTimeout(r, 300));
      const dataUrl = await toPng(mapRef.current, {
        cacheBust: true,
        pixelRatio: 2, // sharper for decks/WhatsApp
        backgroundColor: '#ffffff',
        filter: (node) => {
          // Skip the attribution/zoom controls — cleaner for shareable shots.
          if (node?.classList?.contains('leaflet-control-container')) return false;
          return true;
        },
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${filename}.png`;
      a.click();
      toast.success('PNG downloaded');
    } catch (e) {
      toast.error('Could not export image: ' + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  // Append a short cache-bust version so WhatsApp (and other social caches)
  // fetch a fresh OG preview every time the user re-shares after an edit.
  const withVersion = (u) => {
    if (!u) return u;
    const v = new Date().toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    try {
      const url = new URL(u);
      if (!url.searchParams.has('v')) url.searchParams.set('v', v);
      return url.toString();
    } catch { return u; }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(withVersion(shareUrl));
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const shareWhatsApp = () => {
    if (!shareUrl) return;
    const text = encodeURIComponent(`${whatsappText || ''}\n\n${withVersion(shareUrl)}`.trim());
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={busy}
          data-testid="share-map-trigger"
          className={variant === 'default'
            ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-500/25 font-medium rounded-full px-6 h-10 ring-1 ring-amber-300/60'
            : ''}
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {mapRef && (
          <DropdownMenuItem onClick={downloadPng} data-testid="share-download-png">
            <Download className="w-4 h-4 mr-2" /> Download PNG
          </DropdownMenuItem>
        )}
        {shareUrl && (
          <>
            <DropdownMenuItem onClick={copyLink} data-testid="share-copy-link">
              <Copy className="w-4 h-4 mr-2" /> Copy public link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={shareWhatsApp} data-testid="share-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> Send on WhatsApp
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareMapMenu;
